import React, { useState } from 'react'
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PowerSettingsNew as PowerIcon
} from '@mui/icons-material'
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'

// Define the shape of our data
type TechnologyCompany = {
  id: number
  name: string
  companyId: string
  commission: string
  status: 'Active' | 'Inactive'
  createdBy: string
  createdOn: string
  updatedBy: string
  updatedOn: string
}

// Dummy data with multiple rows for better demonstration
const data: TechnologyCompany[] = [
  {
    id: 1,
    name: 'TechCorp Solutions',
    companyId: 'TC001',
    commission: '15%',
    status: 'Active',
    createdBy: 'John Admin',
    createdOn: '2024-01-15',
    updatedBy: 'Jane Manager',
    updatedOn: '2024-02-10'
  },
  {
    id: 2,
    name: 'Digital Innovations Ltd',
    companyId: 'DI002',
    commission: '12%',
    status: 'Inactive',
    createdBy: 'Mike Smith',
    createdOn: '2024-01-20',
    updatedBy: 'Sarah Wilson',
    updatedOn: '2024-02-15'
  },
  {
    id: 3,
    name: 'Cloud Systems Inc',
    companyId: 'CS003',
    commission: '18%',
    status: 'Active',
    createdBy: 'David Brown',
    createdOn: '2024-02-01',
    updatedBy: 'Lisa Davis',
    updatedOn: '2024-02-20'
  }
]

// Action dropdown component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ActionDropdown = ({ row }: { row: any }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const company = row.original

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    handleClose()
  }

  const handleToggleStatus = () => {
    handleClose()
  }

  return (
    <>
      <IconButton aria-label='more' onClick={handleClick} size='small'>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          <PowerIcon sx={{ mr: 1, fontSize: 18 }} />
          {company.status === 'Active' ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>
    </>
  )
}

// Status chip component
const StatusChip = ({ status }: { status: 'Active' | 'Inactive' }) => {
  return (
    <Chip
      label={status}
      color={status === 'Active' ? 'success' : 'error'}
      size='small'
      variant='filled'
    />
  )
}

// Column helper for type safety
const columnHelper = createColumnHelper<TechnologyCompany>()

// Define columns
const columns = [
  columnHelper.accessor('name', {
    header: 'Technology Company Name',
    cell: (info) => (
      <Typography variant='body2' fontWeight='medium'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('companyId', {
    header: 'Technology Company ID',
    cell: (info) => (
      <Typography variant='body2' fontFamily='monospace'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('commission', {
    header: 'Commission',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => <StatusChip status={info.getValue()} />
  }),
  columnHelper.accessor('createdBy', {
    header: 'Created By',
    cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
  }),
  columnHelper.accessor('createdOn', {
    header: 'Created On',
    cell: (info) => (
      <Typography variant='body2' color='text.secondary'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  }),
  columnHelper.accessor('updatedBy', {
    header: 'Updated By',
    cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
  }),
  columnHelper.accessor('updatedOn', {
    header: 'Updated On',
    cell: (info) => (
      <Typography variant='body2' color='text.secondary'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Action',
    cell: ({ row }) => <ActionDropdown row={row} />
  })
]

export default function OrderSearchTable() {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant='h4' component='h2' gutterBottom>
          Order Search Result
        </Typography>
      </Box>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row, ) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>
              No technology companies found
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  )
}
